import { useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "@/components/page-header";
import { Input } from "@/components/ui/input";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useList } from "@/hooks/use-list";
import { api } from "@/lib/api";
import { Plus, Trash2, Upload } from "lucide-react";

export default function CandidatesPage() {
  const [q, setQ] = useState("");
  const [skill, setSkill] = useState("");
  const qc = useQueryClient();
  const { data, isLoading } = useList<any>("/candidates", { q, skill, pageSize: 50 });

  async function del(id: string, name: string) {
    if (!confirm(`Delete candidate "${name}"?`)) return;
    await api.delete(`/candidates/${id}`);
    qc.invalidateQueries({ queryKey: ["/candidates"] });
  }

  return (
    <>
      <PageHeader
        title="Candidate Database"
        description="Bench, active placements, and pipeline candidates."
        actions={
          <>
            <Button asChild variant="outline"><Link to="/candidates/bench">Bench Aging</Link></Button>
            <Button asChild variant="outline"><Link to="/candidates/import"><Upload className="mr-1 h-4 w-4"/>Import CSV</Link></Button>
            <Button asChild><Link to="/candidates/new"><Plus className="mr-1 h-4 w-4"/>New Candidate</Link></Button>
          </>
        }
      />
      <div className="p-6 space-y-4">
        <div className="flex gap-2">
          <Input placeholder="Search candidates…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-sm" />
          <Input placeholder="Filter by skill…" value={skill} onChange={(e) => setSkill(e.target.value)} className="max-w-xs" />
        </div>
        <div className="rounded-md border bg-card">
          <Table>
            <Thead>
              <Tr>
                <Th>Name</Th><Th>Location</Th><Th>Visa</Th><Th>Bench Status</Th><Th>Skills</Th><Th className="w-10"></Th>
              </Tr>
            </Thead>
            <Tbody>
              {isLoading && <Tr><Td colSpan={6} className="py-8 text-center text-muted-foreground">Loading…</Td></Tr>}
              {!isLoading && (data?.data?.length ?? 0) === 0 && (
                <Tr><Td colSpan={6} className="py-8 text-center text-muted-foreground">No candidates yet.</Td></Tr>
              )}
              {data?.data?.map((c: any) => (
                <Tr key={c.id}>
                  <Td className="font-medium"><Link to={`/candidates/${c.id}`} className="hover:underline">{c.name}</Link></Td>
                  <Td className="text-muted-foreground">{c.location ?? "—"}</Td>
                  <Td>{c.visaStatus ?? "—"}</Td>
                  <Td><Badge variant="secondary">{c.benchStatus}</Badge></Td>
                  <Td className="text-xs text-muted-foreground">
                    {(c.skills ?? []).slice(0, 4).map((s: any) => s.skill).join(", ")}
                  </Td>
                  <Td>
                    <Button variant="ghost" size="icon" onClick={() => del(c.id, c.name)} aria-label="Delete">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </div>
      </div>
    </>
  );
}
